import { tva } from "@gluestack-ui/utils/nativewind-utils";
import React, { useCallback, useMemo } from "react";
import { Platform } from "react-native";
import Animated, {
  FadeInUp,
  FadeOutDown,
  ReduceMotion,
} from "react-native-reanimated";

import { Box } from "@/components/atoms/Box";
import { FlashList, FlashListProps } from "@/components/atoms/FlashList";

import SkeletonActivityFeedItemView from "@/components/organisms/SkeletonActivityFeedItem/SkeletonActivityFeedItemView";
import SkeletonManage from "@/components/organisms/SkeletonManage";
import SkeletonMember from "@/components/organisms/SkeletonMember";
import SkeletonModalPlayer from "@/components/organisms/SkeletonModalPlayer";
import SkeletonNote from "@/components/organisms/SkeletonNote";
import SkeletonPlayer from "@/components/organisms/SkeletonPlayer";
import SkeletonRoster from "@/components/organisms/SkeletonRoster";
import SkeletonShadowTeam from "@/components/organisms/SkeletonShadowTeam";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";
import { TSkeletonListType } from "@/components/organisms/SkeletonManage/SkeletonManageView";

const AnimatedView = Platform.OS === "android" ? Box : Animated.View || Box;

/**
 * Props for the SkeletonListContainer component
 */
interface Props<ItemT = boolean> extends Omit<
  FlashListProps<ItemT>,
  "data" | "renderItem"
> {
  type: TSkeletonListType;
  size?: number;
}

const style = tva({ base: "" });
const contentContainerStyle = tva({ base: "flex-grow p-3" });

/**
 * Container component that renders an animated list of skeleton loading placeholders
 * @param root0 - Component props
 * @param root0.type - Type of skeleton to render (player, roster, note, etc.)
 * @param root0.size - Number of skeleton items to render
 * @param root0.className - Additional CSS classes for the FlatList
 * @param root0.contentContainerClassName - Additional CSS classes for the content container
 * @param root0.ItemSeparatorComponent - Custom separator component between skeleton items
 */
const SkeletonListContainer = ({
  type,
  size = 20,
  className,
  contentContainerClassName,
  ItemSeparatorComponent: ItemSeparatorComponentDefault,
  ...props
}: Props) => {
  const temporaryList = new Array(size).fill(true);
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);

  const bgColor = useMemo(
    () => (isDark ? "bg-surface-strong" : "bg-surface-muted"),
    [isDark]
  );

  const itemBgColor = useMemo(
    () => (isDark ? "bg-outline-emphasis" : "bg-surface-subtle"),
    [isDark]
  );

  const renderSkeleton = useCallback(() => {
    switch (type) {
      case "player":
        return <SkeletonPlayer bgColor={bgColor} />;
      case "manage-roster":
        return <SkeletonRoster bgColor={bgColor} />;
      case "manage-users":
      case "manage-invitations":
      case "manage-orgs":
        return <SkeletonManage type={type} />;
      case "note":
        return <SkeletonNote bgColor={bgColor} itemBgColor={itemBgColor} />;
      case "shadow-team":
        return <SkeletonShadowTeam bgColor={bgColor} />;
      case "shadow-team-member":
        return <SkeletonMember bgColor={bgColor} />;
      case "modal-player":
        return <SkeletonModalPlayer bgColor={bgColor} />;
      case "activity-feed":
        return (
          <SkeletonActivityFeedItemView
            isSmallScreen={isSmallScreen}
            isDark={isDark}
          />
        );
      default:
        return null;
    }
  }, [type, isSmallScreen, isDark, bgColor, itemBgColor]);

  const keyExtractor = useCallback(
    (_: boolean, index: number) => `${type}_${index}`,
    [type]
  );

  return (
    <AnimatedView
      entering={FadeInUp.duration(500).reduceMotion(ReduceMotion.System)}
      exiting={FadeOutDown.duration(500).reduceMotion(ReduceMotion.System)}
    >
      <FlashList
        {...props}
        data={temporaryList}
        keyExtractor={keyExtractor}
        renderItem={renderSkeleton}
        className={style({ className })}
        contentContainerClassName={contentContainerStyle({
          className: contentContainerClassName,
        })}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        ItemSeparatorComponent={ItemSeparatorComponentDefault ?? ItemSeparator}
      />
    </AnimatedView>
  );
};

export default SkeletonListContainer;
